export interface QAEntry {
  tooltip: string // shown on hover — max 2 sentences
  question: string // injected as user message
  answer: string // injected as agent response
}

export type FieldKey =
  | "name"
  | "description"
  | "systemPrompt"
  | "scopeAccess"
  | "email"
  | "link"

// ── Universal fallbacks (all APs) ─────────────────────────────────────────────

const UNIVERSAL: Record<FieldKey, QAEntry> = {
  name: {
    tooltip:
      "The unique identifier of this auto-pilot. It is used for the email address and link and cannot be changed after creation.",
    question: "What does an auto-pilot name mean and why is it immutable?",
    answer:
      "Der Name ist gleichzeitig die eindeutige ID des Auto-Piloten im System – er bestimmt automatisch die E-Mail-Adresse und den Link dieses APs. " +
      "Because other systems and integrations communicate through these addresses, the name cannot be changed after creation.",
  },
  description: {
    tooltip:
      "Die Kernfrage, die dieser Auto-Pilot beantwortet. Sie erscheint als Vorschau-Text auf der Startseite.",
    question: "What is the description of an auto-pilot used for?",
    answer:
      'Die Beschreibung formuliert in einem Satz, welches Problem der Auto-Pilot solves, for example "Which suppliers need attention today?". ' +
      "It appears on the landing page as preview text below the AP name and helps users understand what the AP does at a glance.",
  },
  systemPrompt: {
    tooltip:
      "The core instruction for the AI assistant. It defines tone, focus and chat behavior for this auto-pilot.",
    question:
      "Was bewirkt der Arbeitsablauf und wie passe ich ihn an meinen Anwendungsfall an?",
    answer:
      "Der Arbeitsablauf ist der System-Prompt des KI-Assistenten – er legt fest, welche Rolle der Assistent einnimmt und auf welche Daten er sich bezieht. " +
      "You can adjust it to sharpen the focus or embed specific domain knowledge. Mention the relevant data areas explicitly so the assistant can access your data precisely.",
  },
  scopeAccess: {
    tooltip:
      "Defines which data areas and fields this auto-pilot may read. Individual fields can be enabled or disabled.",
    question: "How does scope access work and why should I restrict it?",
    answer:
      "Der Bereichszugriff steuert, welche Scopes und welche Felder der Auto-Pilot sehen darf. " +
      "Restricting access to relevant data improves answer quality because the assistant is not distracted by unrelated information. Sensitive fields should usually remain disabled.",
  },
  email: {
    tooltip:
      "Die automatisch generierte E-Mail-Adresse dieses Auto-Piloten. Nachrichten an diese Adresse werden direkt im Assistenten verarbeitet.",
    question: "What is an auto-pilot email address used for?",
    answer:
      "Each auto-pilot has its own email address for receiving external messages and attachments. " +
      "For example, if a supplier sends a declaration to this address, it is processed automatically by the AP and imported into the database. The address remains permanently linked to the AP and cannot be changed.",
  },
  link: {
    tooltip:
      "Der direkte Link zu diesem Auto-Piloten. Verwende ihn als Lesezeichen oder binde ihn in externe Workflows ein.",
    question: "What is an auto-pilot link used for?",
    answer:
      "The link allows direct access to this auto-pilot without login navigation. " +
      "You can use it internally as a bookmark or embed it in workflows and dashboards that point directly to this AP. In the future, it will also allow partners to upload data directly to the AP.",
  },
}

const UNIVERSAL_EN: Record<FieldKey, QAEntry> = {
  name: {
    tooltip:
      "The unique identifier of this auto-pilot. It is used for the email address and link and cannot be changed after creation.",
    question: "What does an auto-pilot name mean and why is it immutable?",
    answer:
      "The name is also the unique ID of the auto-pilot in the system. It automatically determines the email address and link of this AP. Because other systems and integrations communicate through these addresses, the name cannot be changed after creation.",
  },
  description: {
    tooltip:
      "The core question this auto-pilot answers. It appears as preview text on the start page.",
    question: "What is the description of an auto-pilot used for?",
    answer:
      "The description states in one sentence which problem the auto-pilot solves, for example “Which suppliers need attention today?”. It appears on the landing page as preview text below the AP name and helps users understand what the AP is responsible for at a glance.",
  },
  systemPrompt: {
    tooltip:
      "The core instruction for the AI assistant. It defines tone, focus, and chat behavior for this auto-pilot.",
    question: "What does the workflow do and how do I adapt it to my use case?",
    answer:
      "The workflow is the system prompt of the AI assistant. It defines which role the assistant takes and which data it should refer to. You can adjust it to sharpen the focus or embed specific domain knowledge. Mention the relevant data areas explicitly so the assistant can access your data precisely.",
  },
  scopeAccess: {
    tooltip:
      "Defines which data areas and fields this auto-pilot may read. Individual fields can be enabled or disabled.",
    question: "How does scope access work and why should I restrict it?",
    answer:
      "Scope access controls which scopes and which fields the auto-pilot can see. Restricting access to relevant data improves answer quality because the assistant is not distracted by unrelated information. Sensitive fields should usually remain disabled.",
  },
  email: {
    tooltip:
      "The automatically generated email address of this auto-pilot. Messages to this address are processed directly by the assistant.",
    question: "What is an auto-pilot email address used for?",
    answer:
      "Each auto-pilot has its own email address for receiving external messages and attachments. For example, if a supplier sends a declaration to this address, it is processed automatically by the AP and imported into the database. The address remains permanently linked to the AP and cannot be changed.",
  },
  link: {
    tooltip:
      "The direct link to this auto-pilot. Use it as a bookmark or embed it into external workflows.",
    question: "What is an auto-pilot link used for?",
    answer:
      "The link allows direct access to this auto-pilot without login navigation. You can use it internally as a bookmark or embed it in workflows and dashboards that point directly to this AP. In the future, it will also allow partners to upload data directly to the AP.",
  },
}

const AP_QA_EN: Record<string, Partial<Record<FieldKey, QAEntry>>> = {
  configuration: {
    systemPrompt: {
      tooltip:
        "Controls how the assistant analyzes documents and proposes database structures. It also defines how strictly it waits for user confirmation.",
      question:
        "How does the workflow affect document analysis in the Configuration AP?",
      answer:
        "In the Configuration AP, the workflow determines how uploaded CSV or Excel files are interpreted and how scopes, fields, and links are derived from them. If you add industry-specific terms such as “RoHS” or “SVHC”, the assistant recognizes these fields as substance-related automatically. Adjust the prompt if the assistant repeatedly assigns scopes incorrectly.",
    },
    scopeAccess: {
      tooltip:
        "The Configuration AP needs access to all areas by default because it owns the initial database structure.",
      question: "Why does the Configuration AP have access to all areas?",
      answer:
        "The Configuration AP is responsible for building the entire database structure: suppliers, substances, bills of materials, products, and customers. Therefore, it needs access to all areas by default. If you want to exclude specific areas from analysis, disable them here.",
    },
  },
  supplier: {
    systemPrompt: {
      tooltip:
        "Defines how the assistant evaluates supplier declarations and prioritizes recommended actions.",
      question: "How can I adapt the supplier AP workflow to my company?",
      answer:
        "You can adapt the prompt to focus on specific supplier categories or deadlines, for example: “Prioritize suppliers with open SVHC declarations older than 90 days”. The more precise the prioritization logic, the more targeted the daily task lists become. Also add relevant internal terms or supplier categories that occur in your data.",
    },
    scopeAccess: {
      tooltip:
        "This AP combines supplier, bill-of-material, and substance data for a complete supply-chain assessment.",
      question: "Which data areas does the supplier AP need and why?",
      answer:
        "The supplier AP combines supplier data (declarations, contacts) with the bill of materials (which components come from which supplier) and substance data (SVHC relevance). Without BOM data, the AP cannot determine which suppliers are responsible for critical components. Customer data is intentionally excluded because it is not needed for supply-chain assessment.",
    },
  },
  "product-conformity": {
    systemPrompt: {
      tooltip:
        "Determines how the assistant evaluates conformity status and which products are classified as critical.",
      question: "How can I adapt the product conformity AP workflow?",
      answer:
        "Add specific directives or standards that apply to your products, such as RoHS, REACH, or WEEE, so the assistant considers them during evaluation. You can also define when a product should count as critically non-compliant.",
    },
    scopeAccess: {
      tooltip:
        "This AP combines product, customer, and BOM data for complete conformity assessment.",
      question: "Why does the product conformity AP need customer data?",
      answer:
        "Conformity requirements are often defined by customers. One customer may require RoHS conformity, another REACH compliance. Without customer assignment, the AP cannot decide which evidence is missing for which product. BOM data is needed to check product substance composition.",
    },
  },
  "customer-conformity": {
    systemPrompt: {
      tooltip:
        "Controls how the assistant identifies missing conformity evidence and sorts it by urgency.",
      question: "How can I adapt the customer conformity AP workflow?",
      answer:
        "Adjust the prompt to match your business prioritization logic, for example: “Customers with active framework agreements have highest priority”. You can also describe specific export formats or cover-letter templates that the assistant should use when creating conformity reports.",
    },
    scopeAccess: {
      tooltip:
        "This AP focuses on customer and product data, supplemented by BOM data for substance checks.",
      question: "Which data areas matter for the customer conformity AP?",
      answer:
        "The customer conformity AP needs customer data (requirements, contacts), product data (conformity status), and BOM data (substance composition). Supplier data is excluded because the focus is the customer relationship. If you need supplier-related information for conformity evidence, enable the supplier area.",
    },
  },
}

// ── Per-AP overrides ──────────────────────────────────────────────────────────

const AP_QA: Record<string, Partial<Record<FieldKey, QAEntry>>> = {
  configuration: {
    systemPrompt: {
      tooltip:
        "Controls how the assistant analyzes documents and proposes database structures. Also defines how strictly it waits for user confirmation.",
      question:
        "How does the workflow affect document analysis in the Configuration AP?",
      answer:
        "In the Configuration AP, the workflow determines how uploaded CSV or Excel files are interpreted and how scopes, fields, and links are derived from them. " +
        'If you add industry-specific terms such as "RoHS" or "SVHC", the assistant recognizes these fields as substance-related automatically. Adjust the prompt if the assistant repeatedly assigns scopes incorrectly.',
    },
    scopeAccess: {
      tooltip:
        "The Configuration AP needs access to all areas by default because it owns the initial database structure.",
      question: "Why does the Configuration AP have access to all areas?",
      answer:
        "The Configuration AP is responsible for building the entire database structure: suppliers, substances, bills of materials, products, and customers. " +
        "Therefore, it needs access to all areas by default. If you want to exclude specific areas from analysis, disable them here.",
    },
  },

  supplier: {
    systemPrompt: {
      tooltip:
        "Defines how the assistant evaluates supplier declarations and prioritizes recommended actions.",
      question: "How can I adapt the supplier AP workflow to my company?",
      answer:
        'You can adapt the prompt to focus on specific supplier categories or deadlines, for example: "Prioritize suppliers with open SVHC declarations older than 90 days". ' +
        "The more precise the prioritization logic, the more targeted the daily task lists become. Also add relevant internal terms or supplier categories that occur in your data.",
    },
    scopeAccess: {
      tooltip:
        "This AP combines supplier, bill-of-material, and substance data for a complete supply-chain assessment.",
      question: "Which data areas does the supplier AP need and why?",
      answer:
        "The supplier AP combines supplier data (declarations, contacts) with the bill of materials (which components come from which supplier) and substance data (SVHC relevance). " +
        "Without BOM data, the AP cannot determine which suppliers are responsible for critical components. Customer data is intentionally excluded because it is not needed for supply-chain assessment.",
    },
  },

  "product-conformity": {
    systemPrompt: {
      tooltip:
        "Determines how the assistant evaluates conformity status and which products are classified as critical.",
      question: "How can I adapt the product conformity AP workflow?",
      answer:
        "Add specific directives or standards that apply to your products (e.g. RoHS, REACH, WEEE) so the assistant considers them during evaluation. " +
        'You can also define when a product should count as "critically non-compliant" — products with ongoing customer claims can be weighted higher automatically.',
    },
    scopeAccess: {
      tooltip:
        "This AP combines product, customer, and BOM data for complete conformity assessment.",
      question: "Why does the product conformity AP need customer data?",
      answer:
        "Conformity requirements are often defined by customers – one customer may require RoHS conformity, another REACH compliance. " +
        "Without customer assignment, the AP cannot decide which evidence is missing for which product. BOM data is needed to check product substance composition.",
    },
  },

  "customer-conformity": {
    systemPrompt: {
      tooltip:
        "Controls how the assistant identifies missing conformity evidence and sorts it by urgency.",
      question: "How can I adapt the customer conformity AP workflow?",
      answer:
        'Adapt the prompt to match your business prioritization logic, for example: "Customers with active framework agreements have highest priority". ' +
        "You can also describe specific export formats or cover-letter templates that the assistant should use when creating conformity reports.",
    },
    scopeAccess: {
      tooltip:
        "This AP focuses on customer and product data, supplemented by BOM data for substance checks.",
      question: "Which data areas matter for the customer conformity AP?",
      answer:
        "The customer conformity AP needs customer data (requirements, contacts), product data (conformity status), and BOM data (substance composition). " +
        "Supplier data is excluded because the focus is the customer relationship. If you need supplier-related information for conformity evidence, enable the supplier area.",
    },
  },
}

export function getQA(
  apId: string,
  field: FieldKey,
  language: string = "DE",
): QAEntry {
  if (language.toUpperCase() === "EN" || language.toUpperCase() === "JA") {
    return AP_QA_EN[apId]?.[field] ?? UNIVERSAL_EN[field]
  }
  return AP_QA[apId]?.[field] ?? UNIVERSAL[field]
}
